import {
  GetLfaConversionFilterType,
  Lfa_Conversion_LastServiceFeedback_Status,
  type Date,
} from '~/graphql/types';
import type { CardStatus } from '../getFrontendStatus';
import getDateAndTimeString from '../getDateAndTimeString';
import getAppointmentDateTimeLabel from '../getAppointmentDateTimeLabel';
import getTodayFormatted from '../getTodayFormatted';
import { isAppointmentInPast } from '../dateUtils';

const text = {
  FEEDBACK_REMINDER: 'Herinnering voor feedback ingesteld op',
  RESCHEDULED: 'Afspraak verplaatst naar',
  APPOINTMENT_PREFIX: 'Afspraak op:',
  MEETING_COMPLETED: 'Afspraak vond plaats op',
  TODAY: 'vandaag',
  AT_TIME: 'om',
  NO_TIME: '(geen tijd afgesproken)',
};

export type LabelStatus = 'snoozed' | 'rescheduled' | 'appointment' | 'done';

export type ReturnType = {
  labelStatus: LabelStatus;
  label: string;
} | null;

type DateLabelArgs = {
  lastServiceFeedbackStatus?: Lfa_Conversion_LastServiceFeedback_Status | null;
  feedbackReminderDate: Date;
  appointmentDate: Date;
  filter: GetLfaConversionFilterType;
  frontendStatus: CardStatus;
  finalFeedbackGivenDate?: string | null;
};

/**
 * Formats date display based on whether it's today and includes time
 */
const formatDateDisplay = (
  dateStr: string,
  timeStr: string | undefined,
  isToday: boolean,
  showNoTimeMessage = true,
): string => {
  const dateDisplay = isToday ? text.TODAY : dateStr;

  if (timeStr) {
    return `${dateDisplay} ${text.AT_TIME} ${timeStr}`;
  }

  if (showNoTimeMessage && !isToday) {
    return `${dateDisplay} ${text.NO_TIME}`;
  }

  return dateDisplay;
};

/**
 * Determines the appropriate date label based on feedback status
 */
const getCardDateLabelByStatus = ({
  lastServiceFeedbackStatus,
  feedbackReminderDate,
  appointmentDate,
  filter,
  frontendStatus,
}: DateLabelArgs): ReturnType => {
  const appointmentDateTime = getDateAndTimeString(appointmentDate);
  const today = getTodayFormatted();
  const isAppointmentTodayWithFilter =
    appointmentDateTime.date === today &&
    filter === GetLfaConversionFilterType.Today;

  // Handle snoozed feedback reminders
  if (
    lastServiceFeedbackStatus ===
    Lfa_Conversion_LastServiceFeedback_Status.Snoozed
  ) {
    const reminderDateTime = getDateAndTimeString(feedbackReminderDate);
    const isReminderToday = reminderDateTime.date === today;

    const dateDisplay = formatDateDisplay(
      reminderDateTime.date,
      reminderDateTime.time,
      isReminderToday,
      false,
    );

    return {
      labelStatus: 'snoozed',
      label: `${text.FEEDBACK_REMINDER} ${dateDisplay}`,
    };
  }

  // Handle rescheduled appointments
  if (
    lastServiceFeedbackStatus ===
    Lfa_Conversion_LastServiceFeedback_Status.Rescheduled
  ) {
    if (isAppointmentInPast(appointmentDate)) {
      const timeDisplay = appointmentDateTime.time
        ? `${text.AT_TIME} ${appointmentDateTime.time}`
        : text.NO_TIME;

      return {
        labelStatus: 'rescheduled',
        label: `${text.APPOINTMENT_PREFIX} ${appointmentDateTime.date} ${timeDisplay}`,
      };
    }

    // For future appointments, use "verplaatst naar" format
    const dateDisplay = formatDateDisplay(
      appointmentDateTime.date,
      appointmentDateTime.time,
      isAppointmentTodayWithFilter,
    );

    return {
      labelStatus: 'rescheduled',
      label: `${text.RESCHEDULED} ${dateDisplay}`,
    };
  }

  // Handle regular appointments (no feedback status yet)
  if (!lastServiceFeedbackStatus) {
    const appointmentLabel = getAppointmentDateTimeLabel({
      date: appointmentDateTime.date,
      time: appointmentDateTime.time,
      filter,
      status: frontendStatus,
    });

    return {
      labelStatus: 'appointment',
      label: appointmentLabel,
    };
  }

  // Handle completed meetings
  if (
    lastServiceFeedbackStatus === Lfa_Conversion_LastServiceFeedback_Status.Done
  ) {
    const dateDisplay = formatDateDisplay(
      appointmentDateTime.date,
      appointmentDateTime.time,
      false,
      false,
    );

    return {
      labelStatus: 'done',
      label: `${text.MEETING_COMPLETED} ${dateDisplay}`,
    };
  }

  return null;
};

export default getCardDateLabelByStatus;
